import React, {
  useState,
  useMemo,
  useContext,
  useRef,
  useEffect,
} from 'react';
import { StyledPre } from 'flow/editor/styles';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectRuleBuilderActiveSubEditor, selectRuleBuilderPanelBoxContainerWidth } from 'flow/store/redux/selectors/ui';
import { getRightAttributeValueAsString } from './helpers';
import {
  computeRuleItemLengthLimit,
  getCommaDelineatedDisplayString,
  getCommaDelineatedValues,
  truncateLabel,
  truncateLabelWithRemainder,
} from '../helpers';
import { ThresholdRightAttributeOrigin } from 'flow/editor/components/NodeEditorDrawer/types';
import { ThresholdContext } from '../context';
import { setRuleBuilderActiveSubEditor } from 'flow/store/redux/slice';
import { RuleBuilderSubEditorSide, RuleBuilderSubEditorType } from 'flow/store/engine/types';
import { RuleBuilderContext } from '../../../context';
import { getIsPreview, isEditable } from '../../../helpers';
import { useUniqueId } from '@usealloy/component-library';
import { useKeyboardKeyEvent } from 'flow/editor/components/WorkflowCanvas/hooks';

function renderListInclusionContent(
  stringifiedValue: string,
  lengthLimit: number,
): React.ReactNode {
  const delineatedValues = getCommaDelineatedValues(stringifiedValue);
  const untruncatedDisplayString = getCommaDelineatedDisplayString(delineatedValues);

  return (
    <StyledPre style={{ fontWeight: 'bold' }}>
      {untruncatedDisplayString.length <= lengthLimit
        ? untruncatedDisplayString
        : truncateLabelWithRemainder(delineatedValues, lengthLimit)}
    </StyledPre>
  );
}

function renderRegexContent(stringifiedValue: string, lengthLimit: number): React.ReactNode {
  return (
    <StyledPre
      style={{
        color: 'var(--cl-pink-600)',
        fontFamily: 'var(--cl-font-family-mono)',
        fontWeight: 'bold',
      }}
    >
      {truncateLabel(stringifiedValue, lengthLimit)}
    </StyledPre>
  );
}
interface RHSIsolatedEditorFieldProps {
  originType: ThresholdRightAttributeOrigin;
}

const RHSIsolatedEditorField = ({
  originType,
}: RHSIsolatedEditorFieldProps) => {
  const dispatch = useAppDispatch();
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const ruleEditorPanelBoxContainerWidth = useAppSelector(selectRuleBuilderPanelBoxContainerWidth);
  const lengthLimit = computeRuleItemLengthLimit(ruleEditorPanelBoxContainerWidth);
  const { editMode, keyboardFocus } = useContext(RuleBuilderContext);
  const { populatedThreshold } = useContext(ThresholdContext);

  const { right: { attributeValue: rightAttributeValue } } = populatedThreshold;

  const ref = useRef(null);
  const id = useUniqueId('isolatedEditorField');

  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    keyboardFocus.register({ id, ref });
    return () => keyboardFocus.unregister(id);
  }, [id, keyboardFocus]);

  const isListInclusion = originType
    === ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_LIST_INCLUSION;

  const displayContent = useMemo(() => {
    if (!rightAttributeValue) {
      const placeholderText = isListInclusion ? 'edit list...' : 'edit regex...';
      return (
        <StyledPre style={{ color: 'var(--cl-gray-700)' }}>
          {placeholderText}
        </StyledPre>
      );
    }

    const stringifiedValue = getRightAttributeValueAsString(rightAttributeValue);

    return isListInclusion
      ? renderListInclusionContent(stringifiedValue, lengthLimit)
      : renderRegexContent(stringifiedValue, lengthLimit);
  }, [
    rightAttributeValue,
    isListInclusion,
    lengthLimit,
  ]);

  function setActiveEditor() {
    keyboardFocus.disable();
    dispatch(
      setRuleBuilderActiveSubEditor({
        thresholdId: populatedThreshold.publicId,
        type: RuleBuilderSubEditorType.ISOLATED_EDITOR,
        side: RuleBuilderSubEditorSide.RHS,
      }),
    );
  }

  function onEnterKey() {
    if (isHighlighted) setActiveEditor();
  }

  useKeyboardKeyEvent('Enter', onEnterKey);

  const isPreview = getIsPreview(
    editMode, populatedThreshold, activeSubEditor.thresholdId,
  );

  return (
    <>
      {isListInclusion && <StyledPre>[</StyledPre>}
      <StyledPre
        id={id}
        onClick={isEditable(editMode) ? setActiveEditor : undefined}
        highlighted={isHighlighted}
        onMouseOver={() => setIsHighlighted(true)}
        onMouseLeave={() => setIsHighlighted(false)}
        onFocus={() => setIsHighlighted(true)}
        onBlur={() => setIsHighlighted(false)}
        tabIndex={0}
        ref={ref}
        style={{
          padding: '0',
          cursor: 'pointer',
          opacity: isPreview ? '0.5' : '1',
          outline: 'none',
        }}
      >
        {displayContent}
      </StyledPre>
      {isListInclusion && <StyledPre>]</StyledPre>}
    </>
  );
};

export default RHSIsolatedEditorField;
